import { getUID } from '@redviking/argonaut-util/src/getUID';
import { ArgoMaterialStatusEnum } from '@redviking/argonaut-util/types/db';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import { accessor } from 'src/store';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const importMaterialUiNodeSpec: NodeSpec<NodeCfg<'importMaterial'>> = {
    kindLabel: 'Import Material',
    nodeCategory: 'material',
    requiredSockets: [ 'trigger', 'modelNumber', 'serialNumber' ],
    cfg: () => ({
        disabled: false,
        controlValues: {
            allowGet: false,
            location: { id: '' },
            status: ArgoMaterialStatusEnum.Pending,
        },
        id: getUID(),
        kind: 'importMaterial',
        width: 220,
    }),
    controls: nodeCfg => ({
        allowGet: defineControlOpts({
            cfg: { kind: 'boolean', label: 'Get or Create' },
            value: nodeCfg.controlValues.allowGet,
            on: { 'update:value': defaultControlUpdateValueHandler },
        }),
        location: defineControlOpts({
            value: nodeCfg.controlValues.location,
            cfg: { kind: 'location' },
            on: { 'update:value': defaultControlUpdateValueHandler },
        }),
        status: defineControlOpts({
            cfg: {
                kind: 'selection',
                label: 'Status',
                items: [
                    { label: 'Pending', value: ArgoMaterialStatusEnum.Pending },
                    { label: 'In Progress', value: ArgoMaterialStatusEnum.InProgress },
                    { label: 'Scrapped', value: ArgoMaterialStatusEnum.Scrapped },
                    { label: 'Quarantined', value: ArgoMaterialStatusEnum.Quarantined },
                    { label: 'Manually Completed', value: ArgoMaterialStatusEnum.ManuallyCompleted },
                    { label: 'Completed', value: ArgoMaterialStatusEnum.Completed },
                ],
            },
            on: { 'update:value': defaultControlUpdateValueHandler },
            value: nodeCfg.controlValues.status,
        }),
    }),
    inputs: () => ({
        trigger: { socket: 'event', label: 'Trigger' },
        modelNumber: { socket: 'string', label: 'Model Number' },
        serialNumber: { socket: 'string', label: 'Serial Number' },
        workOrderId: { socket: 'string', label: 'Work Order ID' },
    }),
    outputs: () => ({
        next: { socket: 'event', label: 'Next' },
        materialId: { socket: 'string', label: 'Material ID' },
    }),
    suppressNode: () => !accessor.testFeatureFlagRequirements([ 'unstableWorkOrders' ]),
    validator: node => {
        if ('deployParamKind' in node.controlValues.location) {
            return node.controlValues.location.deployParamId ? true : { location: 'Location deployment parameter is required' };
        }
        return node.controlValues.location.id ? true : { location: 'Location is required' };
    },
};
